(() => {
  const SELECTORS = {
    root: '[data-hpg-scroll-section]',
    viewport: '[data-hpg-scroll-viewport]',
    previous: '[data-hpg-scroll-prev]',
    next: '[data-hpg-scroll-next]',
  };
  const TRANSITION_DURATION = 1000;
  const PREWARM_ROOT_MARGIN = 800;
  const scrollAnimations = new WeakMap();
  const scrollSnapTypes = new WeakMap();
  const railCleanups = new WeakMap();
  const prewarmObservers = new WeakMap();

  const prewarmMedia = (container) => {
    if (!container) return;

    container.querySelectorAll('img').forEach((image) => {
      image.loading = 'eager';
      image.decoding = 'async';
      if (typeof image.decode === 'function' && !image.complete) {
        image.decode().catch(() => {});
      }
    });

    container.querySelectorAll('video').forEach((video) => {
      video.preload = 'auto';
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      if (video.readyState === 0) video.load();

      const shouldWarmPlay = video.autoplay || video.hasAttribute('autoplay');
      const canAutoplay = video.muted || video.defaultMuted || video.hasAttribute('muted');
      if (shouldWarmPlay && canAutoplay && video.paused) {
        const playResult = video.play();
        if (playResult && typeof playResult.catch === 'function') {
          playResult.catch(() => {});
        }
      }
    });
  };

  const disableSnap = (viewport) => {
    if (!scrollSnapTypes.has(viewport)) {
      scrollSnapTypes.set(viewport, viewport.style.scrollSnapType || '');
    }
    viewport.style.scrollSnapType = 'none';
    viewport.classList.add('is-animating');
  };

  const restoreSnap = (viewport) => {
    if (scrollSnapTypes.has(viewport)) {
      viewport.style.scrollSnapType = scrollSnapTypes.get(viewport);
      scrollSnapTypes.delete(viewport);
    }
    viewport.classList.remove('is-animating');
  };

  const easeInOutCubic = (value) => (
    value < 0.5
      ? 4 * value * value * value
      : 1 - Math.pow(-2 * value + 2, 3) / 2
  );

  const getStep = (viewport) => {
    const track = viewport.querySelector('[data-hpg-scroll-track]') || viewport.firstElementChild;
    const card = viewport.querySelector('[data-hpg-scroll-card]') || track?.firstElementChild;
    if (!card) return Math.round(viewport.clientWidth * 0.8);

    const style = window.getComputedStyle(track || card.parentElement);
    const gap = Number.parseFloat(style.columnGap || style.gap || 0);
    return Math.round(card.getBoundingClientRect().width + gap);
  };

  const prewarmNearbyCards = (viewport) => {
    const track = viewport.querySelector('[data-hpg-scroll-track]') || viewport.firstElementChild;
    const cards = Array.from(viewport.querySelectorAll('[data-hpg-scroll-card]'));
    if (!track || !cards.length) return;

    const viewportRect = viewport.getBoundingClientRect();
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect().left - viewportRect.left);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    [closestIndex, closestIndex + 1].forEach((index) => prewarmMedia(cards[index]));
  };

  const isNearPrewarmRange = (element) => {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    return rect.top < viewportHeight + PREWARM_ROOT_MARGIN && rect.bottom > -PREWARM_ROOT_MARGIN;
  };

  const disconnectPrewarmObserver = (root) => {
    const observer = prewarmObservers.get(root);
    if (!observer) return;

    observer.disconnect();
    prewarmObservers.delete(root);
  };

  const schedulePrewarmNearbyCards = (root, viewport) => {
    if (isNearPrewarmRange(root) || !('IntersectionObserver' in window)) {
      disconnectPrewarmObserver(root);
      prewarmNearbyCards(viewport);
      return;
    }

    if (prewarmObservers.has(root)) return;

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      disconnectPrewarmObserver(root);
      prewarmNearbyCards(viewport);
    }, {
      rootMargin: `${PREWARM_ROOT_MARGIN}px 0px`,
      threshold: 0,
    });

    prewarmObservers.set(root, observer);
    observer.observe(root);
  };

  const setupRail = (root) => {
    const viewport = root.querySelector(SELECTORS.viewport);
    const previous = root.querySelector(SELECTORS.previous);
    const next = root.querySelector(SELECTORS.next);
    if (!viewport || !previous || !next || railCleanups.has(root)) return;

    viewport.dataset.hpgScrollReady = 'true';

    const update = () => {
      const maxScroll = viewport.scrollWidth - viewport.clientWidth - 1;
      previous.disabled = viewport.scrollLeft <= 1;
      next.disabled = viewport.scrollLeft >= maxScroll;
    };

    const scrollToTarget = (target) => {
      const maxScroll = viewport.scrollWidth - viewport.clientWidth;
      const left = Math.max(0, Math.min(target, maxScroll));

      if (scrollAnimations.has(viewport)) {
        window.cancelAnimationFrame(scrollAnimations.get(viewport));
        scrollAnimations.delete(viewport);
        restoreSnap(viewport);
      }

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        viewport.scrollLeft = left;
        update();
        return;
      }

      const start = viewport.scrollLeft;
      const distance = left - start;
      const startedAt = window.performance.now();

      disableSnap(viewport);

      const step = (now) => {
        const elapsed = Math.min((now - startedAt) / TRANSITION_DURATION, 1);
        viewport.scrollLeft = start + distance * easeInOutCubic(elapsed);
        update();

        if (elapsed < 1) {
          scrollAnimations.set(viewport, window.requestAnimationFrame(step));
          } else {
            scrollAnimations.delete(viewport);
            restoreSnap(viewport);
            schedulePrewarmNearbyCards(root, viewport);
          }
      };

      scrollAnimations.set(viewport, window.requestAnimationFrame(step));
    };

    const onPreviousClick = () => {
      scrollToTarget(viewport.scrollLeft - getStep(viewport));
    };

    const onNextClick = () => {
      scrollToTarget(viewport.scrollLeft + getStep(viewport));
    };

    previous.addEventListener('click', onPreviousClick);
    next.addEventListener('click', onNextClick);

    viewport.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    railCleanups.set(root, () => {
      previous.removeEventListener('click', onPreviousClick);
      next.removeEventListener('click', onNextClick);
      viewport.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      delete viewport.dataset.hpgScrollReady;

      if (scrollAnimations.has(viewport)) {
        window.cancelAnimationFrame(scrollAnimations.get(viewport));
        scrollAnimations.delete(viewport);
        restoreSnap(viewport);
      }

      disconnectPrewarmObserver(root);
    });
    update();
    schedulePrewarmNearbyCards(root, viewport);
  };

  const getRoots = (container) => {
    const roots = [];

    if (container.matches?.(SELECTORS.root)) {
      roots.push(container);
    }

    container.querySelectorAll?.(SELECTORS.root).forEach((root) => roots.push(root));

    return roots;
  };

  const setup = (container = document) => {
    getRoots(container).forEach(setupRail);
  };

  const teardown = (container = document) => {
    getRoots(container).forEach((root) => {
      const cleanup = railCleanups.get(root);
      if (!cleanup) return;

      cleanup();
      railCleanups.delete(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => setup());
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));
})();
